import { Injectable } from '@angular/core';
import { BehaviorSubject } from 'rxjs';
import { LocationSetting } from '../ecom-models/location-setting';
import { EComGoogleAnalytics } from './ecom-google-analytics';
import { EComWebStorage } from './ecom-webstorage.services';
import { CompanySetting } from '../ecom-models/company-setting';
import { Discount } from '../ecom-models/discount';
import { UserServiceBase } from '../ecom-base-entities/ecom-stores-base/ecom-userservicebase';
import { User } from '../ecom-models/user';

@Injectable({
    providedIn: 'root'
})
export class EcomSettingService {

    private companySettingSubject = new BehaviorSubject<CompanySetting>(null);
    currentCompanySetting = this.companySettingSubject.asObservable();

    private locationSettingSubject = new BehaviorSubject<LocationSetting>(null);
    currentLocationSetting = this.locationSettingSubject.asObservable();

    private QRCodeTypeSubject = new BehaviorSubject<number>(1);
    currentQRCodeType = this.QRCodeTypeSubject.asObservable();

    private discountsSubject = new BehaviorSubject<Discount[]>([]);
    currentDiscounts = this.discountsSubject.asObservable();

    private userServiceBaseSubject = new BehaviorSubject<UserServiceBase>(undefined);
    currentUserServiceBase = this.userServiceBaseSubject.asObservable();


    private userInfoSubject = new BehaviorSubject<User>(null);
    currentUserInfo = this.userInfoSubject.asObservable();

    constructor(private webstorage: EComWebStorage,
        private eComGoogleAnalytics: EComGoogleAnalytics
    ) {

        const companySetting = this.webstorage.sessionSt_company_settings;
        if (companySetting) {
            this.companySettingSubject.next(companySetting);
        }

        const locationSetting = this.webstorage.sessionSt_location_selected;
        if (locationSetting) {
            this.locationSettingSubject.next(locationSetting);
        }

        const dicounts = this.webstorage.sessionSt_discounts;
        if (dicounts) {
            this.discountsSubject.next(dicounts);
        }

        const userService = this.webstorage.localSt_user_service_request;
        if (userService) {
            this.userServiceBaseSubject.next(userService);
        }

        const qrCodeType = this.webstorage.sessionSt_scanned_table_qr;
        if (qrCodeType) {
            this.QRCodeTypeSubject.next(qrCodeType);
        }

        const userInfo = this.webstorage.sessionSt_user_info;
        if (userInfo) {
            this.userInfoSubject.next(userInfo);
        }
    }

    setCompanySetting(data: CompanySetting) {
        this.companySettingSubject.next(data);
        this.webstorage.sessionSt_company_settings = data;
    }

    getCompanySetting() {
        return this.companySettingSubject.value;
    }

    setLocationSetting(data: LocationSetting) {
        this.locationSettingSubject.next(data);
        this.webstorage.sessionSt_location_selected = data;
    }

    getLocationSetting() {
        return this.locationSettingSubject.value;
    }

    // ---- Short-lived category cache -------------------------------------
    // Categories carry time-dependent availability, so we can't cache them
    // for the whole session. Instead keep a short TTL: navigating back within
    // the window reuses the cache; after it expires we refetch so availability
    // stays correct. Keyed by company|location; a different store misses.
    private readonly CATEGORY_CACHE_TTL_MS = 60 * 1000; // 60,000 ms = 60s = 1m
    private categoryCache: { key: string; expiresAt: number; data: any } | null = null;

    setCategoryCache(key: string, data: any) {
        this.categoryCache = { key, expiresAt: Date.now() + this.CATEGORY_CACHE_TTL_MS, data };
    }

    getCategoryCache(key: string) {
        if (!this.categoryCache || this.categoryCache.key !== key) return null;
        if (Date.now() > this.categoryCache.expiresAt) {
            this.categoryCache = null;
            return null;
        }
        return this.categoryCache.data;
    }

    clearCategoryCache() {
        this.categoryCache = null;
    }

    // 1 => STORE QR, 2 => TABLE QR, 3 => LOCATION QR
    setQRCodeType(value: number) {
        this.QRCodeTypeSubject.next(value);
        this.webstorage.sessionSt_scanned_table_qr = value;
    }

    getQRCodeType() {
        return this.QRCodeTypeSubject.value;
    }

    setDiscounts(data: Discount[]) {
        this.discountsSubject.next(data);
        this.webstorage.sessionSt_discounts = data;
    }

    getDiscounts() {
        return this.discountsSubject.value;
    }


    setUserSerivce(data: UserServiceBase) {
        this.userServiceBaseSubject.next(data);
        this.webstorage.localSt_user_service_request = data;
    }

    getUserSerivce() {
        return this.userServiceBaseSubject.value ? this.userServiceBaseSubject.value : new UserServiceBase();
    }

    getSelectedStore(serviceType: string = 'DELIVERY') {
        const lst = this.getUserSerivce().selectedStoreServiceList ? this.getUserSerivce().selectedStoreServiceList : [];
        return lst.find((obj, index) =>
            //userStoreIndex = index;
            obj.locationPkid === this.getLocationSetting().mm_online_order_location_settings.ools_location_pkid
            && !obj.serviceCompleted
            && obj.serviceType === serviceType
        );
    }


    getUserInfo() {
        return this.userInfoSubject.value;
    }

    setUserInfo(data: User) {
        this.userInfoSubject.next(data);
        this.webstorage.sessionSt_user_info = data;
    }
}
